/**
 * ContractDetail.tsx — silnik formatek (R2, docs/refactor/SILNIK-FORMATEK-ROLLOUT.md
 * — mirror PurchaseInvoiceDetail, Krok 2 unifikacja-formatek).
 *
 * Jeden komponent dla 3 ścieżek:
 *   - /contracts/new      → tryb create (form pusty + edit mode od razu)
 *   - /contracts/:id      → tryb view (read-only, button Edytuj)
 *   - /contracts/:id + Edytuj → tryb edit (PATCH, button Zapisz/Anuluj)
 *
 * Layout (silnik formatek — descent, mirror faktur/pilota commercial/internal):
 *   JEDEN <RecursiveFormRenderer> WSPÓLNY dla create i edit/view, na layoucie
 *   po `topUpDocumentLayout` (gałąź `!effectiveLayoutConfig` usunięta —
 *   top-up gwarantuje 'data' nawet bez aktywnego layoutu,
 *   'parties'/'amendments'/'additional_tables' pochodzą z zaseedowanego
 *   domyślnego layoutu modułu). dokument-blokify (2026-08-24): 'audit_log' NIE jest
 *   już zaszytą zakładką (top-up jej nie wymusza) — to przenośny blok palety
 *   (contract_audit_view) dispatchowany przez contextRenderers, usuwalny/dodawalny
 *   jak inne bloki. Samodzielnej zakładki „Pola dodatkowe" (contract_ext_fields_view)
 *   już nie ma — pole dodatkowe pokazuje się w „Dane" tylko postawione w układzie.
 *   `renderField` z `useKartotekaRenderField(contractEngineConfig, …)` — pola core
 *   Umowy, KAŻDE z własnym rendererem (full override, mirror faktur — patrz
 *   nagłówek `contractCoreRenderers.tsx`). Zakładka 'data' NIE ma nadpisania —
 *   schodzi po układzie (jak u faktur, ADR-0109b). tabRenderers mapuje slug → komponent:
 *       parties     → DocumentPartiesEditor (guard: !isCreate && !!id)
 *       amendments  → ContractAmendmentsEditor (guard: !isCreate && !!id)
 *   contextRenderers → contract_audit_view (przenośny blok, guard !isCreate &&
 *     recordId) + buildDocumentAdditionalRenderers(recordId, { buffered: true, ... })
 *     — tabele dodatkowe (additional_view/satellite_view) dostępne JUŻ w create,
 *     bez HTTP, delty jadą w POST/PATCH.
 *
 * Umowy mają:
 *  - 13 pól wspólnych dokumentu (documents) + 13 pól umowy
 *    (document_contract_details) — wszystkie przez silnik, z układu formularza
 *  - reguły poufności: valueConfidential ⟹ contractValue=null;
 *    subjectMatterConfidential ⟹ subjectMatter=null (wymuszane client-side
 *    w rendererach pól umowy + tutaj przy czyszczeniu payloadu)
 *  - DocumentPartiesEditor + ContractAmendmentsEditor (tylko tryb view/edit na
 *    istniejącym rekordzie) — SEPARATE self-persisting sub-resources, NIE
 *    wchodzą w atomic save (własne przyciski zapisu, orthogonal do tej strony)
 *
 * Moduł: crm_core (ADR-0083; dawniej osobny moduł doc_contracts). System-module slug: contracts.
 *
 * Save flow (atomic, mirror Krok 5 — saveAggregate) — NIEZMIENIONY silnikiem:
 *   - create: POST /contracts z pełnym body (pola umowy + extValues +
 *     additionalTables, gdy niepuste) → jeden zapis w jednej transakcji.
 *   - edit:   PATCH /contracts/:id analogicznie — jedno wywołanie, bez
 *     osobnego PUT na ext-fields (usunięty; ext-fields i delty tabel
 *     dodatkowych jadą inline w body POST/PATCH, patrz saveAggregate).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { UUID_REGEX } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  ArrowLeft,
  FileSignature,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  X as XIcon,
} from 'lucide-react';

import { AttachmentsSection } from '@/components/attachments/AttachmentsSection';
import { buildDocumentPreviewContextRenderer } from '@/components/attachments/DocumentPreviewContextRenderer';
import { DocumentPreviewDockHost } from '@/components/attachments/DocumentPreviewDockHost';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ContractAmendmentsEditor } from '@/components/documents/ContractAmendmentsEditor';
import {
  type ContractFormData,
  EMPTY_CONTRACT_FORM,
} from '@/components/documents/contractCoreRenderers';
import { contractEngineConfig } from '@/components/documents/contractEngineConfig';
import { DocumentItemsEditor } from '@/components/documents/DocumentItemsEditor';
import { DocumentItemsSection } from '@/components/documents/DocumentItemsSection';
import { DocumentLayoutPicker } from '@/components/documents/DocumentLayoutPicker';
import { DocumentPartiesEditor } from '@/components/documents/DocumentPartiesEditor';
import { DocumentTypeChangeBadge } from '@/components/documents/DocumentTypeChangeBadge';
import { DocumentTypeCreateBadge } from '@/components/documents/DocumentTypeCreateBadge';
import {
  type TopUpContext,
  topUpDocumentLayout,
} from '@/components/documents/engine/topUpDocumentLayout';
import {
  type ContextRenderers,
  RecursiveFormRenderer,
} from '@/components/form/RecursiveFormRenderer';
import { SaveFirstNotice } from '@/components/form/SaveFirstNotice';
import { IconButton } from '@/components/IconButton';
import {
  buildCoreFieldRecordValues,
  isSlugMatch,
} from '@/components/kartoteki/engine/engine-config';
import { useKartotekaRenderField } from '@/components/kartoteki/engine/useKartotekaRenderField';
import { ModuleGuard } from '@/components/ModuleGuard';
import { ProcessApprovalBanner } from '@/components/processes/ProcessApprovalBanner';
import { NewRecordDefaultsStatus } from '@/components/record-defaults/NewRecordDefaultsStatus';
import { Spinner } from '@/components/Spinner';
import { SystemModuleAuditTab } from '@/components/system-modules/SystemModuleAuditTab';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { invalidateRecordInstances } from '@/hooks/useActiveInstanceForRecord';
import { useAdditionalTableDeltas } from '@/hooks/useAdditionalTableDeltas';
import { useControlWarning } from '@/hooks/useControlWarning';
import { useDocumentDetailTab } from '@/hooks/useDocumentDetailTab';
import { useDocumentSqlButton } from '@/hooks/useDocumentSqlButton';
import { useDocumentTypeAndLayoutParams } from '@/hooks/useDocumentTypeAndLayoutParams';
import { useFieldErrors } from '@/hooks/useFieldErrors';
import {
  applyCoreDefaults,
  type NewRecordDefaults,
  numberAsString,
  useNewRecordDefaults,
} from '@/hooks/useNewRecordDefaults';
import { useRecordExtValues } from '@/hooks/useRecordExtValues';
import { useSaveBeforeDecision } from '@/hooks/useSaveBeforeDecision';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { buildAdditionalViewRecordValues } from '@/lib/additional-views/record-values';
import { api } from '@/lib/api';
import {
  type DocumentHeaderAmountsFields,
  type DocumentLineItemDraft,
  type DocumentLineItemsFields,
} from '@/lib/documents/document-items';
import {
  draftToPayload,
  serverItemToDraft,
  validateItemsDraft,
} from '@/lib/documents/document-items-draft';
import { contractStatusLabel } from '@/lib/documents/document-select-labels';
import { collectMissingExtRequired } from '@/lib/documents/document-type-fields';
import { useDocumentTypeFields } from '@/lib/documents/useDocumentTypeFields';
import { useNumberingBaseFieldSlug } from '@/lib/documents/useNumberingBaseFieldSlug';
import { useTypeFieldsSaveGuard } from '@/lib/documents/useTypeFieldsSaveGuard';
import { extractErrorCode } from '@/lib/errors';
import { type ProcessWarning, processWarningMessage } from '@/lib/processes/approvals-api';
import { DOCUMENTS_MODULE_CODE } from '@/lib/processes/module-routes';
import { mapDictionarySaveError } from '@/lib/records/save-error-message';
import { useMyModules } from '@/lib/use-my-modules';
import {
  buildExtFieldServerMessages,
  buildFieldIssueMessages,
  buildMissingFieldsDescription,
  buildValidationToastMessage,
  resolveFieldLabel,
} from '@/lib/validation-toast';

import { buildDocumentAdditionalRenderers } from './document-additional-renderers';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ContractDetailRow
  extends ContractFormData, DocumentLineItemsFields, DocumentHeaderAmountsFields {
  id: string;
  companyId: string;
  counterpartyNameCached: string | null;
  counterpartyTaxIdCached: string | null;
  contractorId: string | null;
  valueCached: string | null;
  gcrecord: string | null;
  createdAt: string;
  updatedAt: string;
  createdByUserId: string;
  updatedByUserId: string;
  cruNumber: number | null;
  cruPublishedAt: string | null;
  /** Surowe wartości pól dodatkowych — dołączone przez serwer do odpowiedzi GET rekordu. */
  extValues?: Record<string, unknown>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formFromDetail(d: ContractDetailRow): ContractFormData {
  return {
    title: d.title,
    status: d.status,
    urgency: d.urgency,
    description: d.description,
    notes: d.notes,
    documentDate: d.documentDate,
    source: d.source,
    externalId: d.externalId,
    externalSource: d.externalSource,
    rejectionReason: d.rejectionReason,
    systemNumber: d.systemNumber,
    ownerUserId: d.ownerUserId ?? null,
    categoryId: d.categoryId ?? null,
    contractNumber: d.contractNumber,
    contractNumberMissing: d.contractNumberMissing,
    periodFrom: d.periodFrom,
    periodTo: d.periodTo,
    periodIndefinite: d.periodIndefinite,
    periodDurationValue: d.periodDurationValue,
    periodDurationUnit: d.periodDurationUnit,
    contractValue: d.contractValue,
    contractValueDescription: d.contractValueDescription,
    contractStatus: d.contractStatus,
    subjectMatter: d.subjectMatter,
    subjectMatterConfidential: d.subjectMatterConfidential,
    valueConfidential: d.valueConfidential,
  };
}

/**
 * Stan startowy formularza NOWEJ umowy: wartości startowe z kodu, na nie wartości domyślne pól
 * (Ustawienia kartoteki → Pola). „Czas trwania (wartość)" formularz trzyma jako liczbę; jedyne pole
 * liczbowe trzymane jako tekst to wartość umowy.
 */
function newContractForm(core: NewRecordDefaults['core']): ContractFormData {
  return applyCoreDefaults(EMPTY_CONTRACT_FORM, core, { contractValue: numberAsString });
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ContractDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const isCreate = !id || id === 'new';
  const { data: myModules } = useMyModules();
  const processesActive = myModules?.subscriptions.some((s) => s.code === 'processes') ?? false;
  const navigate = useNavigate();
  // Powrót na listę niesie jej kontekst — patrz useWithMenuItem.
  const withMenuItem = useWithMenuItem();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { me } = useTenantAuth();
  // Podswietlenie pol odrzuconych przez backendowy VALIDATION_ERROR (czerwona
  // ramka, bez tekstu — komunikat zostaje w toascie). Patrz onError w saveMutation
  // + `hasError` przekazywane do useKartotekaRenderField niżej.
  const fe = useFieldErrors();

  // ── Permissions ──
  // /api/auth/me zwraca dziś puste `permissions` (wypełnienie odroczone) — pusta lista
  // oznacza „brak wiedzy", nie „brak uprawnień": pokazujemy akcje, backend egzekwuje.
  const permissionsUnknown = !!me && me.permissions.length === 0;

  const canEdit =
    me?.user.isSuperUser === true ||
    permissionsUnknown ||
    me?.permissions.includes('documents.contract.edit') === true;

  const canManageParties =
    me?.user.isSuperUser === true ||
    permissionsUnknown ||
    me?.permissions.includes('documents.contract.manage_parties') === true;

  const canDelete =
    me?.user.isSuperUser === true ||
    permissionsUnknown ||
    me?.permissions.includes('documents.contract.delete') === true;

  const [searchParams, setSearchParams] = useSearchParams();
  const { tab, setTab } = useDocumentDetailTab();

  // ── Local state ──
  const [editing, setEditing] = useState(isCreate);
  // Rework D.8 — typ wybrany w edycji, niezapisany do czasu głównego zapisu; reset
  // przy zmianie rekordu (mirror layoutOverrideId w useDocumentTypeAndLayoutParams).
  const [pendingDocumentTypeId, setPendingDocumentTypeId] = useState<string | null>(null);
  useEffect(() => {
    setPendingDocumentTypeId(null);
  }, [id]);
  const [form, setForm] = useState<ContractFormData>(() => ({
    ...EMPTY_CONTRACT_FORM,
    documentTypeId: isCreate ? (searchParams.get('documentTypeId') ?? null) : null,
  }));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);

  // Draft edytowalnych pozycji — inicjalizowany z odpowiedzi serwera (efekt niżej),
  // wysyłany jako tablica `items` w tym samym zapisie co dokument. `itemsDirty`
  // steruje semantyką API: wysyłamy `items` TYLKO gdy użytkownik ruszył grid (inaczej
  // pomijamy → backend nie tyka pozycji; bez zbędnego replace-setu przy edycji nagłówka).
  const [itemsDraft, setItemsDraft] = useState<DocumentLineItemDraft[]>([]);
  const [itemsDirty, setItemsDirty] = useState(false);
  // Klucze wierszy niekompletnych z ostatniej próby zapisu (patrz handleSave) —
  // podświetlenie w edytorze, zerowane przy kolejnej udanej walidacji.
  const [invalidItemRowKeys, setInvalidItemRowKeys] = useState<Set<string>>(new Set());
  const handleItemsChange = (next: DocumentLineItemDraft[]) => {
    setItemsDraft(next);
    setItemsDirty(true);
  };

  // Atomic-save refactor (mirror SaleInvoiceDetail): bufor delt
  // tabel dodatkowych — jeden wpis per tableId, agregowany z każdej instancji
  // <AdditionalTableTab buffered> na tej stronie. Wyciągnięte do współdzielonego
  // hooka (Etap 2.2, unifikacja-formatek) — pełny kontrakt i uzasadnienie
  // freshness `getBufferedValue` w nagłówku `useAdditionalTableDeltas.ts`.
  const {
    handleAdditionalDeltasChange,
    getBufferedValue,
    flattenAdditionalDeltas,
    buildAdditionalParentValues,
    resetAdditionalDeltas,
    getAddRowDraft,
    handleAddRowDraftChange,
    resetAddRowDrafts,
  } = useAdditionalTableDeltas();

  // Walidacja klienta przed wysyłką (mirror PurchaseInvoiceDetail — parytet
  // client-side blokady niekompletnych wierszy pozycji): grid pozycji ruszony —
  // zablokuj zapis, gdy któryś wiersz nie ma kompletu pól wymaganych przez
  // backend (patrz `validateItemsDraft`), zamiast wysyłać niekompletny wiersz i
  // pokazywać dopiero odpowiedź 400 serwera.
  // Zwraca, czy zapis wystartował (`false` = zatrzymała go walidacja) — z tego korzysta
  // `useSaveBeforeDecision` (klik decyzji w banerze w edycji = najpierw ten sam zapis).
  function handleSave(): boolean {
    if (blockSaveWhileTypeFieldsLoading()) return false;
    // Braki pól dodatkowych wymaganych w typie dokumentu (bez typu albo bez odczytu pól typu nie
    // sprawdzamy — serwer rozstrzyga) i niekompletne wiersze pozycji zbieramy razem, żeby
    // użytkownik dostał jeden komunikat o wszystkim naraz.
    const missingExt = collectMissingExtRequired({
      fields: kartotekaFields,
      extValues,
      typeFieldStates,
      classFlagFallback: false,
    });
    const invalidRows =
      itemsDirty && itemsDraft.length > 0 ? validateItemsDraft(itemsDraft) : new Set<string>();
    if (missingExt.size > 0 || invalidRows.size > 0) {
      fe.clearAll();
      for (const slug of missingExt) fe.set(slug, t('common.validationIssue.required'));
      setInvalidItemRowKeys(invalidRows);
      const missingDescription = buildMissingFieldsDescription(
        missingExt,
        (key) => resolveFieldLabel(kartotekaFields, key),
        t,
      );
      const rowsMessage = t('tenant.documents.items.editor.rowsIncomplete');
      toast.show({
        variant: 'error',
        title: missingExt.size > 0 ? t('tenant.documents.typeFields.validationError') : rowsMessage,
        description:
          missingExt.size > 0 && invalidRows.size > 0
            ? [missingDescription, rowsMessage].filter(Boolean).join(' ')
            : missingDescription,
      });
      return false;
    }
    setInvalidItemRowKeys(new Set());
    saveMutation.mutate(form);
    return true;
  }

  // ── Fetch detail ────────────────────────────────────────────────────────────

  const detailQ = useQuery({
    queryKey: ['contract', id],
    queryFn: async () => {
      const res = await api.get<{ contract: ContractDetailRow }>(`/contracts/${id}`);
      return res.data.contract;
    },
    enabled: !isCreate && !!id,
  });

  // Wartości domyślne pól (Ustawienia kartoteki → Pola) dla formularza nowej umowy — pola
  // pokazujemy dopiero po ich wczytaniu (patrz `NewRecordDefaultsStatus` niżej).
  const recordDefaults = useNewRecordDefaults('contracts', isCreate);

  // Ext-fields (pola dodatkowe): lokalny stan zasilany wartością z odpowiedzi GET rekordu
  // (`detailQ.data.extValues`, bez osobnego żądania) — pełny kontrakt synchronizacji
  // w nagłówku `useRecordExtValues.ts`. Przy tworzeniu „wartością serwera” są wartości
  // domyślne pól dodatkowych.
  const { extValues, handleExtChange, markSaved, snapshotEdits, discardEdits } = useRecordExtValues(
    {
      recordId: isCreate ? undefined : id,
      serverExtValues: isCreate ? recordDefaults.ext : detailQ.data?.extValues,
      serverVersion: detailQ.dataUpdatedAt,
    },
  );

  // T1.6 (layouty-e1): typ dokumentu do rozstrzygnięcia layoutu — duplikat
  // (walidacja UUID) walidacji `effectiveDocumentTypeId` z
  // `useDocumentTypeAndLayoutParams` niżej — niemożliwe do uniknięcia:
  // ten hook aktywnego layoutu MUSI zostać na stronie (kontrakt Etapu 2.3) i
  // potrzebuje typu PRZED wywołaniem `useDocumentTypeAndLayoutParams` (który
  // z kolei przyjmuje `layoutConfig` STĄD jako `activeLayoutConfig`). Świadomie
  // NIE gate'ujemy hooka poniżej `enabled: false` do czasu załadowania detalu —
  // jeden dodatkowy fetch bez typu (podczas ładowania detalu) + refetch z
  // typem po jego przyjściu, zero ryzyka przedwczesnego renderu fallbacku.
  const rawCreateDocumentTypeIdForLayout = isCreate ? searchParams.get('documentTypeId') : null;
  const documentTypeIdForLayout = isCreate
    ? rawCreateDocumentTypeIdForLayout && UUID_REGEX.test(rawCreateDocumentTypeIdForLayout)
      ? rawCreateDocumentTypeIdForLayout
      : null
    : (pendingDocumentTypeId ?? detailQ.data?.documentTypeId ?? null);

  // dynamic-layout: layout z F12.N buildera + filtrowanie zakładek per rola
  const { layoutConfig, myRoleId, isSuperUser, previewDockEnabled } = useSystemModuleActiveLayout(
    'contracts',
    {
      typeId: documentTypeIdForLayout,
    },
  );

  // Etap 2.3 (unifikacja-formatek): create-params / layout-override cluster
  // wyciągnięty do współdzielonego hooka — pełny kontrakt i uzasadnienie
  // (redirect guard, walidacja UUID, reset override przy zmianie rekordu) w
  // nagłówku `useDocumentTypeAndLayoutParams.ts`.
  const {
    createCompanyId,
    missingDocumentTypeId,
    effectiveDocumentTypeId,
    layoutOverrideId,
    setLayoutOverrideId,
    effectiveLayoutConfig,
  } = useDocumentTypeAndLayoutParams({
    isCreate,
    searchParams,
    navigate,
    listRoute: withMenuItem('/contracts'),
    moduleSlug: 'contracts',
    recordId: id ?? null,
    detailDocumentTypeId: documentTypeIdForLayout,
    activeLayoutConfig: layoutConfig,
  });

  // R2 (silnik formatek): renderField dla pól core (13 wspólnych dokumentu + 13 pól umowy,
  // ADR-0109b) przez contractEngineConfig (full renderer override — mirror faktur, patrz
  // nagłówek contractCoreRenderers.tsx).
  // `hasError` -> fe.get(...) podświetla pole odrzucone przez serwerowy
  // VALIDATION_ERROR (czerwona ramka bez tekstu, patrz onError w saveMutation);
  // wciąż brak walidacji klienckiej pól wymaganych przed wysyłką.
  // Gwiazdka „wymagane" przy polu daty numeracji wybranego typu (patrz PurchaseInvoiceDetail).
  const numberingBaseFieldSlug = useNumberingBaseFieldSlug(
    'contract',
    effectiveDocumentTypeId,
    createCompanyId ?? detailQ.data?.companyId ?? null,
  );

  // Pola dodatkowe wg typu dokumentu (ukrywanie „nie dotyczy”, gwiazdki, sprawdzenie przed wysyłką).
  const { states: typeFieldStates, isLoading: typeFieldsLoading } = useDocumentTypeFields(
    effectiveDocumentTypeId,
    createCompanyId ?? detailQ.data?.companyId ?? null,
  );
  // Zapis wstrzymany, dopóki trwa wczytywanie pól typu (patrz `useTypeFieldsSaveGuard`).
  const blockSaveWhileTypeFieldsLoading = useTypeFieldsSaveGuard(typeFieldsLoading);

  const { renderField, fields: kartotekaFields } = useKartotekaRenderField(contractEngineConfig, {
    form,
    onChange: setForm,
    extValues,
    onExtChange: handleExtChange,
    editable: editing,
    isCreate,
    isFieldExtraRequired: isSlugMatch(numberingBaseFieldSlug),
    typeFieldStates,
    typeFieldsPending: typeFieldsLoading,
    companyId: createCompanyId ?? detailQ.data?.companyId ?? null,
    hasError: (key) => !!fe.get(String(key)),
    getError: (key) => fe.get(String(key)),
    clearError: (key) => fe.clear(String(key)),
  });

  // Top-up (bezwarunkowy, idempotentny) na layoucie EFEKTYWNYM — gwarantuje
  // zakładkę 'data' (jedyną w pageTabs) gdy brak aktywnego layoutu w DB
  // (edge case — w praktyce moduł 'contracts' ma zawsze zaseedowany domyślny
  // layout formularza z tabs 'parties'/'amendments'/'additional_tables', mirror
  // pattern faktur 'lines'/'accounting'/'documents' — te NIE są gwarantowane
  // przez top-up, wymagają seeded layoutu). 'data' schodzi po układzie (bez
  // nadpisania strony, ADR-0109b): pole — także dodatkowe — pokazuje się tylko,
  // gdy stoi w układzie (top-up niczego nie dokleja).
  const topUpCtx = useMemo<TopUpContext>(
    () => ({
      // dokument-blokify (2026-08-24): 'ext_fields'/'audit_log' USUNIĘTE z pageTabs —
      // wcześniej top-up dosypywał je bezwarunkowo przy każdym renderze (nawet po świadomym
      // usunięciu przez usera w builderze), co łamało portability. Teraz blok audytu jest
      // zasiany jako NORMALNE dziecko w domyślnym (seedowanym) layoucie `contracts` (mirror
      // kartotek); pól dodatkowych top-up ani seed nie dokleja — są tylko tam, gdzie stoją w układzie.
      // Self-heal dla pristine layoutów dzieje się przy re-seedzie, nie w runtime.
      pageTabs: [{ slug: 'data', title: t('tenant.documents.shared.tabs.data'), sortOrder: 0 }],
      coreFieldSlugs: contractEngineConfig.coreFieldSlugs,
    }),
    [t],
  );
  const engineLayout = useMemo(
    () => topUpDocumentLayout(effectiveLayoutConfig, topUpCtx),
    [effectiveLayoutConfig, topUpCtx],
  );

  // Sync server → local form. W trybie create formularz startuje z wartości domyślnych pól
  // (nadpisują wartości startowe z kodu); documentTypeId z URL nakładamy PO nich.
  useEffect(() => {
    if (isCreate) {
      setForm({
        ...newContractForm(recordDefaults.core),
        documentTypeId: searchParams.get('documentTypeId') ?? null,
      });
      return;
    }
    // Rework D.8 — nie nadpisuj niezapisanych zmian w trakcie edycji: odświeżenie
    // detalu w tle (inna mutacja/zakładka unieważniająca ten klucz) nie może cofnąć
    // edytowanego formularza. Po zapisie efekt dogania świeży stan serwera, bo
    // `editing` wraca na false w tym samym onSuccess co invalidate.
    if (editing) return;
    if (detailQ.data) {
      setForm(formFromDetail(detailQ.data));
    }
    // documentTypeId czytamy wyłącznie przy pierwszym mount'cie / SPA-nawigacji na /new;
    // dołączenie searchParams powodowałoby reset formularza przy każdej zmianie taba.
    // `recordDefaults.core` zmienia referencję raz (wczytanie) — pola są wtedy jeszcze ukryte.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchParams świadomie pominięty (patrz wyżej)
  }, [detailQ.data, isCreate, editing, recordDefaults.core]);

  // Sync server → draft pozycji (przy załadowaniu detalu i po zapisie). Gdy użytkownik
  // ma lokalne zmiany (`itemsDirty`) — draft zostaje nietknięty. Po zapisie onSuccess
  // zeruje `itemsDirty`, więc ten efekt resynchronizuje ze świeżym stanem serwera.
  useEffect(() => {
    if (itemsDirty) return;
    setItemsDraft((detailQ.data?.items ?? []).map(serverItemToDraft));
  }, [detailQ.data?.items, itemsDirty]);

  // ── Save mutation ───────────────────────────────────────────────────────────

  // Klik decyzji w banerze procesu, gdy formularz jest w edycji: najpierw ten sam zapis co
  // „Zapisz", okno decyzji dopiero po jego sukcesie (useSaveBeforeDecision; „zapis już trwa"
  // sprawdza sam hak po kluczu `saveMutationKey` mutacji zapisu tej kopii formularza).
  // `succeeded` / `aborted` są wołane z `saveMutation.onSuccess/onError` i z anulowania
  // ostrzeżenia kontroli niżej.
  const saveBeforeDecision = useSaveBeforeDecision({
    editing,
    recordId: id,
    save: handleSave,
  });

  // Przycisk SQL (klocek `sql_button`): zadania w tle na tej umowie — stan, pasek blokady, renderer
  // klocka, utworzenie → zadanie → przejście (patrz nagłówek `useDocumentSqlButton`).
  const sqlButton = useDocumentSqlButton({
    recordClass: 'contract',
    isCreate,
    recordId: id,
    layoutConfig: effectiveLayoutConfig,
    save: handleSave,
    createCompanyId,
    saveBeforeDecision,
  });

  // Kontrola dokumentu (F20+ reużywalny hook, patrz useControlWarning) — `mutate`
  // celowo owinięty w lambdę: `saveMutation` deklarowana niżej, ale to wywołanie
  // trafia dopiero do zamknięcia `handleConfirm`, uruchamianego po pełnym mount'cie.
  const controlWarning = useControlWarning<ContractFormData & { warningsAccepted?: boolean }>({
    mutate: (payload) => saveMutation.mutate(payload),
    // Anulowane ostrzeżenie kontroli = zapis się nie odbył → okno decyzji się nie otwiera, a zadanie
    // przycisku SQL kliknięte na nowej umowie nie rusza.
    onCancel: sqlButton.onSaveAborted,
  });

  const saveMutation = useMutation({
    mutationKey: saveBeforeDecision.saveMutationKey,
    mutationFn: async (data: ContractFormData & { warningsAccepted?: boolean }) => {
      // Apply confidentiality rules before sending (belt-and-suspenders after
      // the contract-field renderers already enforce them in state)
      const payload: Record<string, unknown> = {
        ...(data.title ? { title: data.title } : {}),
        status: data.status,
        urgency: data.urgency,
        description: data.description || null,
        notes: data.notes || null,
        documentDate: data.documentDate || null,
        source: data.source,
        ownerUserId: data.ownerUserId || null,
        categoryId: data.categoryId ?? null,
        ...(!isCreate
          ? {
              externalId: data.externalId || null,
              externalSource: data.externalSource || null,
              rejectionReason: data.rejectionReason || null,
            }
          : {}),
        // satellite
        contractNumber: data.contractNumberMissing ? null : data.contractNumber || null,
        contractNumberMissing: data.contractNumberMissing,
        periodFrom: data.periodIndefinite ? null : data.periodFrom || null,
        periodTo: data.periodIndefinite ? null : data.periodTo || null,
        periodIndefinite: data.periodIndefinite,
        periodDurationValue: data.periodDurationValue,
        periodDurationUnit: data.periodDurationUnit || null,
        contractValue: data.valueConfidential ? null : data.contractValue || null,
        contractValueDescription: data.contractValueDescription || null,
        contractStatus: data.contractStatus || null,
        subjectMatter: data.subjectMatterConfidential ? null : data.subjectMatter || null,
        subjectMatterConfidential: data.subjectMatterConfidential,
        valueConfidential: data.valueConfidential,
      };

      if (isCreate) {
        payload.documentTypeId = data.documentTypeId || null;
      } else if (
        pendingDocumentTypeId !== null &&
        pendingDocumentTypeId !== detailQ.data?.documentTypeId
      ) {
        payload.documentTypeId = pendingDocumentTypeId;
      }

      // Kontrola dokumentu (tenant hook `document_control`) — ostrzeżenie miękkie
      // ponawiane z tą flagą po potwierdzeniu w dialogu (patrz useControlWarning).
      if (data.warningsAccepted) {
        payload.warningsAccepted = true;
      }

      // Atomic-save refactor (mirror SaleInvoiceDetail): jeden zapis —
      // extValues + delty tabel dodatkowych jadą w TYM SAMYM body co pola
      // umowy (backend zapisuje wszystko w jednej transakcji, patrz
      // saveAggregate). Pola opcjonalne — pomijamy gdy puste, zachowanie
      // identyczne jak dotąd dla callerów bez nich.
      // Migawka edycji tuż przed włożeniem `extValues` do żądania — PRZED oczekiwaniem na
      // definicje pól tabel (`buildAdditionalParentValues`): edycja zrobiona w tym czasie nie
      // trafia do body, więc `markSaved()` w onSuccess jej nie zdejmie (nagłówek `useRecordExtValues.ts`).
      snapshotEdits();
      if (Object.keys(extValues).length > 0) {
        payload.extValues = extValues;
      }
      const additionalTablesBody = flattenAdditionalDeltas();
      if (additionalTablesBody) {
        payload.additionalTables = additionalTablesBody;
        // Wartości pól umowy z ekranu dla wiązań `parent_field` w wierszach tabel dodatkowych
        // — OBOK delt, liczone ze stanu przekazanego do zapisu (`data`), tylko skalary.
        const additionalParentValues = await buildAdditionalParentValues(
          buildAdditionalViewRecordValues(
            extValues,
            buildCoreFieldRecordValues(contractEngineConfig, data),
          ),
          'contract',
        );
        if (additionalParentValues) {
          payload.additionalParentValues = additionalParentValues;
        }
      }

      // Increment 2: pozycje jadą w TYM SAMYM body (replace-set; kwoty pochodne i
      // amount_* przelicza silnik serwera w tej samej transakcji). Wysyłamy TYLKO gdy
      // grid ruszony — inaczej pomijamy `items` (backend nie tyka pozycji). Pusty draft
      // po ruszeniu = `[]` = kasacja pozycji + powrót do trybu kwot ręcznych.
      if (itemsDirty) {
        payload.items = itemsDraft.map(draftToPayload);
      }

      let recordId: string;
      let processWarning: ProcessWarning | undefined;
      if (isCreate) {
        // CORE INVARIANT: w trybie „Wszystkie firmy" interceptor NIE wysyła
        // X-Company-Id (brak aktywnej firmy) — bez jawnego nagłówka backend
        // (`requireCompany`) odrzuciłby POST z MISSING_HEADER, nawet gdy
        // documentTypeId jednoznacznie wskazuje firmę. Jawny nagłówek per-request
        // wygrywa z interceptorem (patrz `lib/api.ts` — nie nadpisuje już ustawionego
        // X-Company-Id) i działa identycznie w trybie jednej aktywnej firmy.
        const res = await api.post<{
          id: string;
          systemNumber: string | null;
          processWarning?: ProcessWarning;
        }>(
          '/contracts',
          payload,
          createCompanyId ? { headers: { 'X-Company-Id': createCompanyId } } : undefined,
        );
        recordId = res.data.id;
        processWarning = res.data.processWarning;
      } else {
        // companyId is immutable — never send in PATCH
        await api.patch(`/contracts/${id}`, payload);
        recordId = id!;
      }

      return { id: recordId, processWarning };
    },
    onSuccess: async (data) => {
      // Udany zapis gasi podświetlenia z poprzedniej nieudanej próby (ramka + tekst).
      fe.clearAll();
      void queryClient.invalidateQueries({ queryKey: ['contracts'] });
      // Zapis mógł zmienić typ dokumentu (a od niego zależą prawa obiegu): flagi „Wyślij do
      // akceptacji" / „Anuluj proces" odświeżamy RAZEM z rekordem (bez czekania na nie).
      void invalidateRecordInstances(queryClient, DOCUMENTS_MODULE_CODE, data.id);
      // Awaitowane: bez tego setItemsDirty(false)/setEditing(false) (niżej) flipowały
      // stan PRZED odświeżeniem detalu, co na chwilę pokazywało stare pozycje przed
      // resyncem draftu (post-save stale-items flash). Reszta invalidacji zostaje
      // fire-and-forget — tylko ta jedna gatekeeperuje UI po zapisie pozycji.
      // Pola dodatkowe zapisane razem z dokumentem — od tej chwili wartość serwera (z odświeżonego
      // detalu) może je nadpisać; bez tego guard niezapisanych edycji trzymałby stare wartości.
      markSaved();
      await queryClient.invalidateQueries({ queryKey: ['contract', data.id] });
      // Atomic-save refactor (mirror SaleInvoiceDetail): po zapisie odśwież
      // rekordy tabel dodatkowych (prefix match — łapie wszystkie
      // ['additional-records', tableId, parentId]) i wyczyść lokalny bufor
      // delt — backend już je zapisał atomowo.
      void queryClient.invalidateQueries({ queryKey: ['additional-records'] });
      resetAdditionalDeltas();
      // Finding [G] (pre-commit-verify): szkic niezatwierdzonego add-row też czyścimy
      // po udanym zapisie — spójnie z buforem zatwierdzonych wierszy powyżej.
      resetAddRowDrafts();
      // Pozycje zapisane atomowo — zeruj dirty, żeby efekt sync odświeżył draft ze
      // świeżego stanu serwera po refetchu detalu.
      setItemsDirty(false);

      if (isCreate) {
        toast.show({
          variant: 'success',
          title: t('tenant.documents.contracts.detail.toast.created'),
        });
        if (data.processWarning) {
          toast.show({
            variant: 'warning',
            title: processWarningMessage(t, data.processWarning),
          });
        }
        // Przycisk SQL kliknięty na nowej umowie: zadanie rusza TERAZ — z id nowej umowy, PRZED
        // przejściem pod jej adres. Porażka zlecenia dostaje własny komunikat; umowa już istnieje,
        // więc przejście i tak następuje.
        await sqlButton.runPendingCreateRun(data.id);
        // Karta „nowy" ma zamienić się w kartę zapisanego rekordu (replaceTab) —
        // reset do podglądu, bo create→detail nie remontuje strony.
        setEditing(false);
        navigate(withMenuItem(`/contracts/${data.id}`), { state: { replaceTab: true } });
        return;
      }
      toast.show({
        variant: 'success',
        title: t('tenant.documents.contracts.detail.toast.updated'),
      });
      setPendingDocumentTypeId(null);
      setEditing(false);
      // Zapis wywołany kliknięciem decyzji w banerze → teraz otwórz okno decyzji (no-op, gdy
      // zapis poszedł zwykłym „Zapisz"). Tylko gałąź edycji — przy tworzeniu nie ma banera.
      saveBeforeDecision.succeeded();
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      // Zapis wywołany kliknięciem decyzji w banerze: każdy błąd poza ostrzeżeniem kontroli
      // kończy sprawę (okno decyzji się nie otworzy). O `CONTROL_WARNING` rozstrzyga user w
      // dialogu: potwierdzenie ponawia zapis (okno otworzy jego sukces), anulowanie woła
      // `onCancel`. Na samym początku, żeby żadna późniejsza gałąź (ani rzut) nie ominęła tego.
      // To samo dla zadania przycisku SQL klikniętego na nowej umowie: utworzenie się nie udało → nie rusza.
      if (code !== 'CONTROL_WARNING') sqlButton.onSaveAborted();
      // Podświetlenie + tekst pod polem odrzuconym przez serwer musi zobaczyć KAŻDĄ
      // odpowiedź, zanim jakakolwiek gałąź zrobi wczesny `return` (patrz JSDoc
      // `useFieldErrors.ts`). Kolejność komunikatu: (1) polski z issue Zoda, (2) własny
      // komunikat serwera z `details` (reguły krzyżowe — polskie zdania bez `issues`),
      // (3) generyk. Angielskie default-y Zoda nie przeciekają (pola z issue mają wpis
      // w fieldMsgs i wygrywają przed messages[0]).
      const fieldMsgs = buildFieldIssueMessages(err, t);
      fe.applyServerError(err, {
        translate: (field, messages) =>
          fieldMsgs[field] ?? messages[0] ?? t('common.validationIssue.generic'),
      });
      // Kontrola dokumentu (CONTROL_ERROR/CONTROL_WARNING/CONTROL_HOOK_FAILED) ma
      // pierwszeństwo — jeśli obsłużona (toast lub dialog już pokazany), reszta gałęzi
      // (specyficzne dla umów) nie musi się odpalać.
      if (controlWarning.handleError(err, form)) return;
      // Zapis odrzucony, bo na umowie trwa zadanie przycisku SQL — czytelny komunikat z opisem zadania
      // zamiast ogólnego „nie udało się zapisać”; odświeżony stan zadań pokaże pasek blokady.
      if (sqlButton.handleLockedError(err)) return;
      // Odrzucona część słownikowa zapisu (pozycja spoza słownika / brak lub zła wartość
      // parametru słownika) — czytelna przyczyna; przed ogólną gałęzią 400 poniżej.
      const dictionaryMsg = mapDictionarySaveError(err, t);
      if (dictionaryMsg !== null) {
        toast.show({ variant: 'error', title: dictionaryMsg });
        return;
      }
      if (code === 'TYPE_CHANGE_BLOCKED_CRU') {
        toast.show({
          variant: 'error',
          title: t('tenant.documents.typePicker.errors.TYPE_CHANGE_BLOCKED_CRU'),
        });
        return;
      }
      if (code === 'COMPANY_ID_IMMUTABLE') {
        toast.show({
          variant: 'error',
          title: t('tenant.documents.contracts.detail.toast.companyImmutable'),
        });
        return;
      }
      if (code === 'SUBJECT_MATTER_CONFIDENTIAL_CONFLICT') {
        toast.show({
          variant: 'error',
          title: t('tenant.documents.contracts.detail.toast.subjectMatterConflict'),
        });
        return;
      }
      if (code === 'VALUE_CONFIDENTIAL_CONFLICT') {
        toast.show({
          variant: 'error',
          title: t('tenant.documents.contracts.detail.toast.valueConflict'),
        });
        return;
      }
      if (isAxiosError(err) && err.response?.status === 400) {
        const specific = buildValidationToastMessage(
          err,
          (field) => {
            const key = `tenant.documents.contracts.detail.field.${field}`;
            return i18n.exists(key) ? t(key) : null;
          },
          t,
        );
        toast.show({
          variant: 'error',
          title: specific ?? t('tenant.documents.contracts.detail.toast.validationError'),
          // Brak wymaganego pola dodatkowego serwer zgłasza w `details` (bez `issues`), więc
          // `specific` jest wtedy puste — nazwij brakujące pola komunikatem serwera.
          description: specific ? undefined : buildExtFieldServerMessages(err, kartotekaFields),
        });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('tenant.documents.contracts.detail.toast.genericError'),
      });
    },
  });

  // ── Delete mutation ─────────────────────────────────────────────────────────

  const deleteMutation = useMutation({
    mutationFn: async (vars: { warningsAccepted?: boolean }) => {
      const query = vars.warningsAccepted ? '?warningsAccepted=true' : '';
      await api.delete(`/contracts/${id}${query}`);
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.documents.contracts.detail.toast.deleted'),
      });
      void queryClient.invalidateQueries({ queryKey: ['contracts'] });
      navigate(withMenuItem('/contracts'));
    },
    onError: (err, vars) => {
      // Kontrola dokumentu (CONTROL_ERROR/CONTROL_WARNING/CONTROL_HOOK_FAILED) ma
      // pierwszeństwo — jeśli obsłużona (toast lub dialog już pokazany), reszta gałęzi
      // (generyczny błąd usunięcia) nie musi się odpalać.
      if (deleteControlWarning.handleError(err, vars)) return;
      // Usuwanie odrzucone na czas zadania przycisku SQL — jak przy zapisie (opis zadania + pasek blokady).
      if (sqlButton.handleLockedError(err)) return;
      toast.show({
        variant: 'error',
        title: t('tenant.documents.contracts.detail.toast.deleteError'),
      });
    },
  });

  // Kontrola dokumentu dla flow DELETE (mirror flow SAVE, klucze i18n dedykowane
  // — patrz useControlWarning nagłówek). Deklarowana PO deleteMutation, więc
  // `mutate` w lambdzie zamyka się nad już zdefiniowaną mutacją.
  const deleteControlWarning = useControlWarning<{ warningsAccepted?: boolean }>({
    mutate: (payload) => deleteMutation.mutate(payload),
    titleKey: 'tenant.documents.controlWarning.deleteTitle',
    confirmLabelKey: 'tenant.documents.controlWarning.deleteConfirmLabel',
  });

  // ── Restore mutation ────────────────────────────────────────────────────────

  const restoreMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/contracts/${id}/restore`, {});
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.documents.contracts.detail.toast.restored'),
      });
      void queryClient.invalidateQueries({ queryKey: ['contracts'] });
      void queryClient.invalidateQueries({ queryKey: ['contract', id] });
    },
    onError: (err) => {
      // Przywrócenie odrzucone na czas zadania przycisku SQL — opis zadania + pasek blokady.
      if (sqlButton.handleLockedError(err)) return;
      toast.show({
        variant: 'error',
        title: t('tenant.documents.contracts.detail.toast.restoreError'),
      });
    },
  });

  // ── Cancel ──────────────────────────────────────────────────────────────────

  function handleCancel() {
    fe.clearAll();
    setPendingDocumentTypeId(null);
    if (isCreate) {
      navigate(withMenuItem('/contracts'));
      return;
    }
    if (detailQ.data) {
      setForm(formFromDetail(detailQ.data));
    }
    discardEdits();
    // Anuluj porzuca też zbuforowane zmiany tabel dodatkowych i szkice add-row (nie tylko zapis).
    resetAdditionalDeltas();
    resetAddRowDrafts();
    setEditing(false);
  }

  // ── Loading / error states ──────────────────────────────────────────────────

  // Redirect w toku (obsługiwany w useDocumentTypeAndLayoutParams) — nie renderuj formularza create
  // ani na moment, żeby user nie zobaczył migawki formularza bez typu dokumentu.
  if (missingDocumentTypeId) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="contract-detail-loading">
        <Spinner />
      </div>
    );
  }

  if (!isCreate && detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="contract-detail-loading">
        <Spinner />
      </div>
    );
  }

  if (!isCreate && detailQ.isError) {
    return (
      <div className="space-y-4 p-6">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="h-4 w-4" aria-hidden="true" />}
          onClick={() => navigate(withMenuItem('/contracts'))}
        >
          {t('tenant.documents.contracts.detail.backToList')}
        </Button>
        <div className="rounded-xl border border-status-error-border bg-status-error-bg p-6 text-sm text-status-error">
          {t('tenant.documents.contracts.detail.notFound')}
        </div>
      </div>
    );
  }

  if (isCreate && recordDefaults.status !== 'ready') {
    return <NewRecordDefaultsStatus defaults={recordDefaults} testIdPrefix="contract-detail" />;
  }

  const detail = detailQ.data;
  const isDeleted = !isCreate && !!detail?.gcrecord;
  const recordId = isCreate ? null : (id ?? null);
  const dockCompanyId = createCompanyId ?? detail?.companyId ?? undefined;

  // ── Render ──────────────────────────────────────────────────────────────────

  // Renderery bloków dokumentu (załączniki, podgląd, tabele dodatkowe, audyt, pozycje) — dla
  // głównego renderera, także w zakładce „Dane". Liczone przy KAŻDYM renderze (bez memo):
  // `getBufferedValue` musi być bieżący (patrz komentarz przy tabelach dodatkowych).
  const contractContextRenderers: ContextRenderers = {
    attachments_view: () =>
      recordId ? (
        <AttachmentsSection
          ownerType="document"
          ownerId={recordId}
          companyId={createCompanyId ?? detail?.companyId ?? undefined}
        />
      ) : null,
    ...buildDocumentPreviewContextRenderer({
      ownerType: 'document',
      ownerId: recordId,
      companyId: createCompanyId ?? detail?.companyId ?? undefined,
      extValues,
    }),
    ...buildDocumentAdditionalRenderers(recordId, {
      editable: editing,
      buffered: true,
      processesActive,
      beforeDecision: saveBeforeDecision.beforeDecision,
      documentTypeId: detail?.documentTypeId ?? null,
      onDeltasChange: handleAdditionalDeltasChange,
      getAddRowDraft,
      onAddRowDraftChange: handleAddRowDraftChange,
      // Data-loss bugfix (mirror CommercialDocDetail/InternalDocDetail):
      // pass the hook's `getBufferedValue` (recreated on every Map
      // change) into this un-memoized factory call (it runs fresh every
      // render), so a Tabs-driven remount of AdditionalTableTab reads
      // the intact CURRENT buffer instead of an empty one. See
      // AdditionalTableTab header + useAdditionalTableDeltas.ts.
      getBufferedValue,
      companyId: createCompanyId ?? detail?.companyId ?? null,
      // F22 (widoki dodatkowe) — recordValues dla wiązania `record_field`: ext (żywe,
      // działa w create) + core przez resolver silnika formatek.
      extValues,
      coreRecordValues: buildCoreFieldRecordValues(contractEngineConfig, form),
      // Klucz wiązań `parent_field` w tabelach dodatkowych tej klasy dokumentu.
      parentKey: 'contract',
    }),
    // Klocek „Przycisk SQL": zadanie w tle na tej umowie. Prawo = ten sam sygnał co „Edytuj” (usunięta
    // umowa nie ma „Edytuj”, więc i przycisk jest wyszarzony); serwer i tak sprawdza prawo edycji z typem
    // i etapem procesu.
    sql_button: sqlButton.renderSqlButton({
      canEdit: canEdit && !isDeleted,
      savePending: saveMutation.isPending,
    }),
    // dokument-blokify (2026-08-24): przenośny blok „Audit log" — mirror kartotek. Guard
    // `!isCreate && recordId` (w trybie create `id==='new'`). Samodzielnej zakładki „Pola
    // dodatkowe" już nie ma: pole dodatkowe edytuje się w „Dane" tylko postawione w układzie,
    // a wartości przychodzą z odpowiedzi GET rekordu.
    contract_audit_view: () =>
      !isCreate && recordId ? (
        <SystemModuleAuditTab
          moduleSlug="contracts"
          recordId={recordId}
          testIdPrefix="contract-audit"
        />
      ) : (
        <SaveFirstNotice message={t('tenant.documents.contracts.detail.tabDisabled.save')} />
      ),
    document_items_view: ({ element }) =>
      editing ? (
        <DocumentItemsEditor
          value={itemsDraft}
          onChange={handleItemsChange}
          currencyCode={detail?.currencyCode}
          companyId={createCompanyId ?? detail?.companyId ?? null}
          invalidRowKeys={invalidItemRowKeys}
          columnSettings={element.settings}
        />
      ) : (
        <DocumentItemsSection
          items={detail?.items}
          vatBreakdown={detail?.vatBreakdown}
          amountNet={detail?.amountNet}
          amountVat={detail?.amountVat}
          amountGross={detail?.amountGross}
          currencyCode={detail?.currencyCode}
          columnSettings={element.settings}
        />
      ),
  };

  return (
    <ModuleGuard moduleCode="crm_core">
      <DocumentPreviewDockHost
        enabled={previewDockEnabled && !!recordId}
        moduleId="contracts"
        ownerType="document"
        ownerId={recordId}
        companyId={dockCompanyId}
      >
        <div className="space-y-4 p-6" data-testid="contract-detail-page">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <IconButton
                aria-label={t('tenant.documents.contracts.detail.backToList')}
                data-testid="contract-detail-back"
                onClick={() => navigate(withMenuItem('/contracts'))}
              >
                <ArrowLeft />
              </IconButton>
              <div className="flex items-start gap-3">
                <FileSignature className="mt-1 h-6 w-6 text-text-secondary" aria-hidden="true" />
                <div>
                  {isCreate ? (
                    <h1
                      className="text-2xl font-semibold text-text-primary"
                      data-testid="contract-detail-create-title"
                    >
                      {t('tenant.documents.contracts.detail.createTitle')}
                      <DocumentTypeCreateBadge
                        docClass="contract"
                        documentTypeId={effectiveDocumentTypeId}
                        companyId={createCompanyId}
                        testId="contract-detail-create-type-badge"
                        onChange={(newTypeId, newCompanyId) => {
                          const params = new URLSearchParams(searchParams);
                          params.set('documentTypeId', newTypeId);
                          params.set('companyId', newCompanyId);
                          setSearchParams(params, { replace: true });
                          setForm((prev) => ({ ...prev, documentTypeId: newTypeId }));
                        }}
                      />
                    </h1>
                  ) : (
                    <>
                      <h1
                        className="text-2xl font-semibold text-text-primary"
                        data-testid="contract-detail-title"
                      >
                        {detail?.systemNumber ??
                          detail?.contractNumber ??
                          form.title ??
                          t('tenant.documents.contracts.detail.untitled')}
                        {canEdit && (
                          <DocumentTypeChangeBadge
                            docClass="contract"
                            documentTypeId={pendingDocumentTypeId ?? detail?.documentTypeId ?? null}
                            companyId={detail?.companyId ?? null}
                            testId="contract-detail-change-type-badge"
                            disabled={!editing || !!(detail?.cruNumber || detail?.cruPublishedAt)}
                            disabledReason={
                              detail?.cruNumber || detail?.cruPublishedAt
                                ? t('tenant.documents.typePicker.errors.TYPE_CHANGE_BLOCKED_CRU')
                                : undefined
                            }
                            onConfirm={(newTypeId) => {
                              setPendingDocumentTypeId(newTypeId);
                              setLayoutOverrideId(null);
                            }}
                          />
                        )}
                        {pendingDocumentTypeId &&
                          pendingDocumentTypeId !== detail?.documentTypeId && (
                            <span
                              className="ml-2 rounded bg-accent-50 px-2 py-0.5 align-middle text-xs font-normal text-accent-700"
                              data-testid="contract-detail-pending-type-number-hint"
                            >
                              {t('tenant.documents.typePicker.numberPendingOnSave')}
                            </span>
                          )}
                      </h1>
                      <div className="mt-1 flex items-center gap-3 text-xs text-text-tertiary">
                        {form.contractStatus && (
                          <span className="rounded bg-accent-50 px-2 py-0.5 text-accent-700">
                            {contractStatusLabel(form.contractStatus, t)}
                          </span>
                        )}
                        {detail?.counterpartyNameCached && (
                          <span>{detail.counterpartyNameCached}</span>
                        )}
                        {detail?.valueCached && !form.valueConfidential && (
                          <span className="tabular-nums">{detail.valueCached}</span>
                        )}
                        {form.valueConfidential && (
                          <span className="italic text-text-tertiary">
                            {t('tenant.documents.contracts.detail.valueConfidentialBadge')}
                          </span>
                        )}
                        {isDeleted && (
                          <span className="rounded bg-red-50 px-2 py-0.5 text-red-600 dark:bg-red-900/20 dark:text-red-400">
                            {t('tenant.documents.contracts.detail.archivedBadge')}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              <DocumentLayoutPicker
                moduleSlug="contracts"
                value={layoutOverrideId}
                onChange={setLayoutOverrideId}
                myRoleId={myRoleId}
                isSuperUser={isSuperUser}
              />
              {editing ? (
                <>
                  <Button
                    onClick={handleCancel}
                    variant="secondary"
                    leftIcon={<XIcon size={14} aria-hidden="true" />}
                    disabled={saveMutation.isPending}
                    data-testid="contract-detail-cancel-btn"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleSave}
                    variant="primary"
                    leftIcon={
                      isCreate ? (
                        <Plus size={14} aria-hidden="true" />
                      ) : (
                        <Save size={14} aria-hidden="true" />
                      )
                    }
                    loading={saveMutation.isPending}
                    disabled={saveMutation.isPending}
                    data-testid="contract-detail-save-btn"
                  >
                    {isCreate ? t('common.create') : t('common.save')}
                  </Button>
                </>
              ) : isDeleted ? (
                canDelete && (
                  <Button
                    onClick={() => setConfirmRestore(true)}
                    variant="primary"
                    leftIcon={<RotateCcw size={14} aria-hidden="true" />}
                    loading={restoreMutation.isPending}
                    disabled={restoreMutation.isPending}
                    data-testid="contract-detail-restore-btn"
                  >
                    {t('tenant.documents.contracts.actions.restore')}
                  </Button>
                )
              ) : (
                <>
                  {canDelete && (
                    <Button
                      onClick={() => setConfirmDelete(true)}
                      variant="danger"
                      disabled={sqlButton.recordLocked}
                      leftIcon={<Trash2 size={14} aria-hidden="true" />}
                      data-testid="contract-detail-delete-btn"
                    >
                      {t('common.delete')}
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      onClick={() => setEditing(true)}
                      disabled={sqlButton.recordLocked}
                      leftIcon={<Pencil size={14} aria-hidden="true" />}
                      data-testid="contract-detail-edit-btn"
                    >
                      {t('common.edit')}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Zadanie przycisku SQL na umowie (w kolejce lub w toku): umowy nie da się zapisać ani
            usunąć, „Edytuj” i „Usuń” są nieaktywne; pasek znika po zakończeniu zadania. */}
          {sqlButton.lockBanner}

          {/* Process approval banner */}
          {!isCreate && id && processesActive ? (
            <ProcessApprovalBanner
              moduleCode={DOCUMENTS_MODULE_CODE}
              recordId={id}
              typeId={detail?.documentTypeId ?? null}
              beforeDecision={saveBeforeDecision.beforeDecision}
            />
          ) : null}

          {/* R2 (silnik formatek): create i edit/view współdzielą JEDEN
          RecursiveFormRenderer na layoucie po top-upie (mirror faktur/pilota —
          gałąź `!effectiveLayoutConfig` usunięta). 'data' schodzi po układzie
          (ADR-0109b): każde pole — wspólne, umowy i dodatkowe — przez
          `renderField` z silnika, tylko jeśli stoi w układzie. Tabele dodatkowe
          (additional_view/satellite_view) dostępne od razu w trybie
          buforowanym (bez HTTP, delty jadą w POST/PATCH — patrz saveAggregate),
          'parties'/'amendments' zostają gated na !isCreate && id
          (self-persisting sub-resources, orthogonal do atomic save),
          blok audytu pokazuje przed zapisem komunikat „zapisz najpierw"
          (wymaga zapisanego recordId). */}
          <RecursiveFormRenderer
            layoutConfig={engineLayout}
            activeTabId={tab}
            onTabChange={setTab}
            myRoleId={myRoleId}
            isSuperUser={isSuperUser}
            renderField={renderField}
            tabRenderers={{
              parties: () =>
                !isCreate && id ? (
                  <DocumentPartiesEditor
                    documentId={id}
                    canManage={canManageParties}
                    companyId={detail?.companyId ?? null}
                  />
                ) : null,
              amendments: () =>
                !isCreate && id ? (
                  <ContractAmendmentsEditor documentId={id} canManage={canEdit} />
                ) : null,
            }}
            contextRenderers={contractContextRenderers}
          />

          {/* Confirm delete */}
          <ConfirmDialog
            open={confirmDelete}
            title={t('common.confirmDelete')}
            message={t('tenant.documents.contracts.detail.deleteConfirmBody', {
              number: detail?.systemNumber ?? detail?.contractNumber ?? form.title ?? id,
            })}
            confirmLabel={t('common.delete')}
            variant="danger"
            onConfirm={() => {
              setConfirmDelete(false);
              deleteMutation.mutate({});
            }}
            onClose={() => setConfirmDelete(false)}
          />

          {/* Confirm restore */}
          <ConfirmDialog
            open={confirmRestore}
            title={t('tenant.documents.contracts.detail.restoreConfirmTitle', {
              number: detail?.systemNumber ?? detail?.contractNumber ?? form.title ?? id,
            })}
            message={t('tenant.documents.contracts.detail.restoreConfirmBody')}
            confirmLabel={t('tenant.documents.contracts.actions.restore')}
            variant="primary"
            onConfirm={() => {
              setConfirmRestore(false);
              restoreMutation.mutate();
            }}
            onClose={() => setConfirmRestore(false)}
          />

          {/* Kontrola dokumentu — ostrzeżenia miękkie (SAVE + DELETE) */}
          {controlWarning.dialog}
          {deleteControlWarning.dialog}
        </div>
      </DocumentPreviewDockHost>
    </ModuleGuard>
  );
}
